// API Client for StreamShare Remote Visual Assist Platform
// Handles all HTTP requests to the Hono backend API

interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  success: boolean;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  role: 'admin' | 'agent' | 'supervisor';
  createdAt: string;
  updatedAt: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  subscription: {
    plan: 'free' | 'pro' | 'enterprise';
    status: 'active' | 'inactive' | 'trial';
    expiresAt?: string;
  };
  settings: {
    maxAgents: number;
    recordingSessions: boolean;
    customBranding: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

interface Session {
  id: string;
  code: string;
  organizationId: string;
  agentId: string;
  customerId?: string;
  status: 'waiting' | 'active' | 'paused' | 'completed' | 'cancelled';
  title: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
  startedAt?: string;
  endedAt?: string;
  duration?: number;
  recordingUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface CollaboratorInvite {
  sessionId: string;
  email: string;
  name: string;
  role: 'collaborator';
  invitedBy: string;
  invitedAt: string;
}

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl: string = import.meta.env.VITE_API_URL || 'https://streamshare-functions-preview-preview.iserialize.workers.dev/api') {
    this.baseUrl = baseUrl;
    this.authToken = localStorage.getItem('auth-token');
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.authToken) {
      defaultHeaders['Authorization'] = `Bearer ${this.authToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.message || data.error || 'An error occurred'
        };
      }

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('API Request failed:', error);
      return {
        success: false,
        error: 'Network error. Please check your connection.'
      };
    }
  }

  // Authentication Methods
  async login(email: string, password: string): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.request<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    if (response.success && response.data) {
      this.authToken = response.data.token;
      localStorage.setItem('auth-token', response.data.token);
    }

    return response;
  }

  async register(userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    organizationName: string;
  }): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.request<{ user: User; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });

    if (response.success && response.data) {
      this.authToken = response.data.token;
      localStorage.setItem('auth-token', response.data.token);
    }

    return response;
  }

  async logout(): Promise<void> {
    this.authToken = null;
    localStorage.removeItem('auth-token');
    await this.request('/auth/logout', { method: 'POST' });
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.request<User>('/auth/me');
  }

  // Organization Methods
  async getOrganization(): Promise<ApiResponse<Organization>> {
    return this.request<Organization>('/organizations/current');
  }

  async updateOrganization(data: Partial<Organization>): Promise<ApiResponse<Organization>> {
    return this.request<Organization>('/organizations/current', {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  // Session Management
  async createSession(data: {
    title: string;
    description?: string;
    customerName?: string;
    customerEmail?: string;
  }): Promise<ApiResponse<Session>> {
    // Transform the data to match backend expectations
    const sessionData = {
      customer_note: data.description
    };
    
    return this.request<Session>('/sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData)
    });
  }

  async getSessions(params?: {
    status?: Session['status'];
    agentId?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{ sessions: Session[]; total: number }>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/sessions${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    return this.request<{ sessions: Session[]; total: number }>(endpoint);
  }

  async getSession(sessionId: string): Promise<ApiResponse<Session>> {
    return this.request<Session>(`/sessions/${sessionId}`);
  }

  async getSessionByCode(code: string): Promise<ApiResponse<Session>> {
    return this.request<Session>(`/sessions/code/${code}`);
  }

  async updateSession(
    sessionId: string, 
    data: Partial<Session>
  ): Promise<ApiResponse<Session>> {
    return this.request<Session>(`/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async startSession(sessionId: string): Promise<ApiResponse<Session>> {
    return this.request<Session>(`/sessions/${sessionId}/start`, {
      method: 'POST'
    });
  }

  async endSession(sessionId: string): Promise<ApiResponse<Session>> {
    return this.request<Session>(`/sessions/${sessionId}/end`, {
      method: 'POST'
    });
  }

  async pauseSession(sessionId: string): Promise<ApiResponse<Session>> {
    return this.request<Session>(`/sessions/${sessionId}/pause`, {
      method: 'POST'
    });
  }

  async resumeSession(sessionId: string): Promise<ApiResponse<Session>> {
    return this.request<Session>(`/sessions/${sessionId}/resume`, {
      method: 'POST'
    });
  }

  // Collaboration Methods
  async inviteCollaborator(data: {
    sessionId: string;
    email: string;
    name: string;
  }): Promise<ApiResponse<CollaboratorInvite>> {
    return this.request<CollaboratorInvite>('/sessions/collaborate/invite', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async joinAsCollaborator(sessionCode: string): Promise<ApiResponse<Session>> {
    return this.request<Session>(`/sessions/collaborate/join/${sessionCode}`, {
      method: 'POST'
    });
  }

  // Customer Methods
  async validateCustomerSession(code: string): Promise<ApiResponse<{
    session: Session;
    agent: User;
    organization: Organization;
  }>> {
    return this.request<{
      session: Session;
      agent: User;
      organization: Organization;
    }>(`/sessions/customer/validate/${code}`);
  }

  async joinCustomerSession(
    sessionId: string,
    customerData: {
      name: string;
      email?: string;
    }
  ): Promise<ApiResponse<Session>> {
    return this.request<Session>(`/sessions/customer/join/${sessionId}`, {
      method: 'POST',
      body: JSON.stringify(customerData)
    });
  }

  // LiveKit Token Generation
  async generateLiveKitToken(data: {
    sessionId: string;
    participantName: string;
    role: 'agent' | 'customer' | 'supervisor' | 'collaborator';
  }): Promise<ApiResponse<{ token: string; roomName: string }>> {
    return this.request<{ token: string; roomName: string }>('/sessions/livekit-token', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Billing Methods
  async getBillingInfo(): Promise<ApiResponse<{
    subscription: Organization['subscription'];
    usage: {
      currentPeriodSessions: number;
      currentPeriodMinutes: number;
      limits: {
        maxSessions: number;
        maxMinutes: number;
      };
    };
  }>> {
    return this.request('/billing/info');
  }

  async createCheckoutSession(priceId: string): Promise<ApiResponse<{ url: string }>> {
    return this.request<{ url: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ priceId })
    });
  }

  async createPortalSession(): Promise<ApiResponse<{ url: string }>> {
    return this.request<{ url: string }>('/billing/portal', {
      method: 'POST'
    });
  }

  // Analytics Methods
  async getSessionAnalytics(params?: {
    startDate?: string;
    endDate?: string;
    agentId?: string;
  }): Promise<ApiResponse<{
    totalSessions: number;
    totalDuration: number;
    averageDuration: number;
    successfulSessions: number;
    sessionsById: Array<{
      date: string;
      sessions: number;
      duration: number;
    }>;
  }>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/analytics/sessions${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    return this.request(endpoint);
  }
}

// Create and export a singleton instance
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://streamshare-functions-preview-preview.iserialize.workers.dev';
export const apiClient = new ApiClient(`${API_BASE_URL}/api`);

// Export types for use in components
export type {
  User,
  Organization,
  Session,
  CollaboratorInvite,
  ApiResponse
};
